import type { UserSearchFilter } from "@dutyon/apis";
import { CloseButton, Group, Loader, TextInput, useDebouncedCallback } from "@dutyon/ui-kit";
import { Search } from "lucide-react";
import { useEffect, useState } from "react";

interface UserSearchBoxProps {
  value: UserSearchFilter;
  onChange: (value: UserSearchFilter) => void;
  loading?: boolean;
}

export function UserSearchBox({ value, onChange, loading = false }: UserSearchBoxProps) {
  const [name, setName] = useState(value.name || "");
  const [email, setEmail] = useState(value.email || "");
  const [phone, setPhone] = useState(value.phone || "");
  const [isDebouncing, setIsDebouncing] = useState(false);

  useEffect(() => {
    setName(value.name || "");
  }, [value.name]);

  useEffect(() => {
    setEmail(value.email || "");
  }, [value.email]);

  useEffect(() => {
    setPhone(value.phone || "");
  }, [value.phone]);

  const debouncedOnChange = useDebouncedCallback((nextFilter: UserSearchFilter) => {
    setIsDebouncing(false);
    onChange(nextFilter);
  }, 350);

  const handleFieldChange = (field: keyof UserSearchFilter, val: string) => {
    setIsDebouncing(true);
    let nextFilter: UserSearchFilter;
    if (field === "name") {
      setName(val);
      nextFilter = { name: val, email, phone };
    } else if (field === "email") {
      setEmail(val);
      nextFilter = { name, email: val, phone };
    } else {
      setPhone(val);
      nextFilter = { name, email, phone: val };
    }
    debouncedOnChange(nextFilter);
  };

  const handleClear = (field: keyof UserSearchFilter) => {
    setIsDebouncing(false);
    if (field === "name") {
      setName("");
      onChange({ name: "", email, phone });
    } else if (field === "email") {
      setEmail("");
      onChange({ name, email: "", phone });
    } else {
      setPhone("");
      onChange({ name, email, phone: "" });
    }
  };

  const isSearching = loading || isDebouncing;

  const renderRightSection = (field: keyof UserSearchFilter, val: string) => {
    if (isSearching) {
      return <Loader size={14} color="dimmed" />;
    }
    if (val) {
      return (
        <CloseButton
          size="sm"
          variant="transparent"
          onClick={() => handleClear(field)}
          aria-label={`${field} 검색어 초기화`}
        />
      );
    }
    return null;
  };

  return (
    <Group gap="xs" wrap="wrap">
      <TextInput
        placeholder="이름"
        leftSection={<Search size={16} />}
        leftSectionPointerEvents="none"
        rightSection={renderRightSection("name", name)}
        rightSectionPointerEvents={isSearching ? "none" : "auto"}
        value={name}
        onChange={(e) => handleFieldChange("name", e.currentTarget.value)}
        w={{ base: "100%", sm: 130 }}
        size="sm"
      />
      <TextInput
        placeholder="이메일"
        leftSection={<Search size={16} />}
        leftSectionPointerEvents="none"
        rightSection={renderRightSection("email", email)}
        rightSectionPointerEvents={isSearching ? "none" : "auto"}
        value={email}
        onChange={(e) => handleFieldChange("email", e.currentTarget.value)}
        w={{ base: "100%", sm: 170 }}
        size="sm"
      />
      <TextInput
        placeholder="전화번호"
        leftSection={<Search size={16} />}
        leftSectionPointerEvents="none"
        rightSection={renderRightSection("phone", phone)}
        rightSectionPointerEvents={isSearching ? "none" : "auto"}
        value={phone}
        onChange={(e) => handleFieldChange("phone", e.currentTarget.value)}
        w={{ base: "100%", sm: 150 }}
        size="sm"
      />
    </Group>
  );
}
